import React from "react";
import { Divider } from "@mui/material";

export interface SplitPayment {
  cashAmount: number;
  cardAmount: number;
}

interface Props {
  paymentMethod: string;
  isCompleted: boolean;
  onPaymentMethodChange: (method: string) => void;
  themeBorderColor: string;
  themeTextColor: string;
  getLightBgClass: () => string;
  total: number;
  splitPayment: SplitPayment;
  onSplitPaymentChange: (split: SplitPayment) => void;
}

const PaymentMethodSelector: React.FC<Props> = ({
  paymentMethod,
  isCompleted,
  onPaymentMethodChange,
  themeBorderColor,
  themeTextColor,
  getLightBgClass,
  total,
  splitPayment,
  onSplitPaymentChange,
}) => {
  const methods = [
    { method: "Card", label: "Card" },
    { method: "Cash", label: "Cash" },
    { method: "Split", label: "Split" },
  ];

  const handleDecimalChange = (value: string, field: keyof SplitPayment) => {
    if (!/^\d*\.?\d*$/.test(value)) return;

    const numValue = Number(value) || 0;

    // Calculate the maximum allowed value based on the other field
    // (e.g., if Cash is 30 and Total is 100, Card can't be more than 70)
    const otherFieldValue = field === "cashAmount" ? splitPayment.cardAmount : splitPayment.cashAmount;
    const maxAllowed = Number((total - otherFieldValue).toFixed(2));

    // If the user tries to enter a value above the max allowed, clamp it to the max
    const clampedValue = numValue > maxAllowed ? maxAllowed : numValue;

    onSplitPaymentChange({
      ...splitPayment,
      [field]: clampedValue,
    });
  };

  const splitSum = Number((splitPayment.cashAmount + splitPayment.cardAmount).toFixed(2));

  const remaining = Number((total - splitSum).toFixed(2));
  const isSplitInvalid = paymentMethod === "Split" && remaining !== 0;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-1 mb-1.5 mt-1.5 w-full">
      <h3 className="text-base font-semibold text-gray-800 text-center mb-0 tracking-wide">Choose Payment Method</h3>

      <div className="grid grid-cols-3 gap-2">
        {methods.map(({ method, label }) => {
          const isSelected = paymentMethod === method;
          return (
            <label
              key={method}
              className={`flex items-center justify-center gap-1 rounded-lg border px-2 py-1.5 transition-all duration-150 cursor-pointer
                ${
                  isSelected
                    ? `${themeBorderColor} ${getLightBgClass()} shadow-sm scale-[1.01]`
                    : "border-gray-300 hover:border-gray-400 bg-gray-50"
                }
                ${isCompleted ? "opacity-60 cursor-not-allowed" : ""}
              `}
            >
              <input
                type="radio"
                name="payment-method"
                value={method}
                checked={isSelected}
                onChange={() => onPaymentMethodChange(method)}
                disabled={isCompleted}
                className="w-3.5 h-3.5 cursor-pointer"
              />
              <span className={`text-xs sm:text-sm font-medium ${isSelected ? themeTextColor : "text-gray-700"}`}>{label}</span>
            </label>
          );
        })}
      </div>

      {/* Inline Split Payment Inputs */}
      {paymentMethod === "Split" && !isCompleted && (
        <div className="mt-2 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-2 border border-gray-200 space-y-2">
          <Divider className="mb-1" />
          <p className="text-xs font-semibold text-gray-700 text-center">Split Payment</p>

          {/* Cash Input */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700">Cash Amount</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              className="w-24 border rounded px-2 py-1 text-right text-sm"
              value={splitPayment.cashAmount === 0 ? "" : splitPayment.cashAmount}
              onChange={(e) => handleDecimalChange(e.target.value, "cashAmount")}
            />
          </div>

          {/* Card Input */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700">Card Amount</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              className="w-24 border rounded px-2 py-1 text-right text-sm"
              value={splitPayment.cardAmount === 0 ? "" : splitPayment.cardAmount}
              onChange={(e) => handleDecimalChange(e.target.value, "cardAmount")}
            />
          </div>

          <Divider />

          {/* Total and Remaining Validation */}
          <div className="space-y-1 mt-1">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-800">Total</span>
              <span className="text-sm font-semibold text-gray-800">{total.toFixed(2)}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-800">Remaining</span>
              <span className={`text-sm font-bold ${isSplitInvalid ? "text-red-600" : "text-green-600"}`}>{remaining.toFixed(2)}</span>
            </div>
          </div>

          {isSplitInvalid && <p className="text-xs text-red-600 text-center">Cash + Card amounts must equal the total.</p>}
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelector;
